/**
 * Test Cycles Page (Sprint 9 — Quality Hub)
 *
 * Repository view for hybrid test cycles — groups of manual + automated tests.
 * Features:
 *  - Project selector
 *  - Data table with expandable rows showing cycle items
 *  - AUTOMATED items: status badge + execution link
 *  - MANUAL items: status badge + "Play" button → ManualExecutionDrawer
 */

import { useReducer, useState, Fragment, useEffect, useRef, useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Dialog, DialogPanel, Transition, TransitionChild } from '@headlessui/react';
import axios from 'axios';
import {
    Layers, Plus, Loader2, AlertCircle, FolderKanban,
    CheckCircle2, XCircle, Clock, ChevronDown, ChevronRight,
    Play, Bot, ClipboardCheck, SkipForward, Eye, FileText, Trash2, X,
    GitBranch, Cpu, Search, CheckSquare,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useProjectContext } from '../context/ProjectContext';
import { CycleBuilderDrawer } from '../components/CycleBuilderDrawer';
import { ManualExecutionDrawer } from '../components/ManualExecutionDrawer';
import { ExecutionDrawer } from '../components/ExecutionDrawer';
import type { Execution } from '../types';

// ── Constants ─────────────────────────────────────────────────────────────────

const isProduction =
    window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1';
const API_URL = isProduction ? import.meta.env.VITE_API_URL ?? '' : 'http://localhost:3000';

// ── Types ─────────────────────────────────────────────────────────────────────

interface IProject {
    id: string;
    name: string;
    slug: string;
}

interface IManualStep {
    id: string;
    action: string;
    expectedResult: string;
    status: 'PENDING' | 'PASSED' | 'FAILED' | 'SKIPPED';
    comment?: string;
}

interface ICycleItem {
    id: string;
    testCaseId: string;
    type: 'MANUAL' | 'AUTOMATED';
    title: string;
    status: string;
    executionId?: string;
    manualSteps?: IManualStep[];
}

interface ICycleRow {
    _id: string;
    name: string;
    status: 'PENDING' | 'RUNNING' | 'COMPLETED';
    /** Discriminates hosted runs from external CI passive runs. */
    source?: 'agnox-hosted' | 'external-ci';
    items: ICycleItem[];
    summary: {
        total: number;
        passed: number;
        failed: number;
        automationRate: number;
    };
    createdAt: string;
    ciContext?: {
        source: 'github' | 'gitlab' | 'azure' | 'bitbucket' | 'jenkins' | 'webhook' | 'local';
        repository?: string;
        branch?: string;
        prNumber?: number;
        commitSha?: string;
        runUrl?: string;
    };
}

type TabId = 'manual' | 'ci';

// ── Reducer ───────────────────────────────────────────────────────────────────

interface CyclePageState {
    selectedProjectId: string;
    isDrawerOpen: boolean;
    expandedCycleId: string | null;
    manualDrawer: {
        isOpen: boolean;
        cycleId: string;
        /** The cycle's own status — used to decide isEditable. */
        cycleStatus: 'PENDING' | 'RUNNING' | 'COMPLETED';
        itemId: string;
        itemTitle: string;
        itemStatus: string;
        steps: IManualStep[];
    };
    reviewExecutionId: string | null;
    /** ID of the automated item whose test-results panel is expanded (drill-down). */
    expandedItemId: string | null;
}

type CyclePageAction =
    | { type: 'SET_PROJECT'; projectId: string }
    | { type: 'TOGGLE_DRAWER'; isOpen: boolean }
    | { type: 'TOGGLE_EXPAND'; cycleId: string }
    | { type: 'OPEN_MANUAL'; cycleId: string; cycleStatus: 'PENDING' | 'RUNNING' | 'COMPLETED'; itemId: string; itemTitle: string; itemStatus: string; steps: IManualStep[] }
    | { type: 'CLOSE_MANUAL' }
    | { type: 'SET_REVIEW_EXECUTION'; executionId: string | null }
    | { type: 'TOGGLE_ITEM_EXPAND'; itemId: string };

const initialState: CyclePageState = {
    selectedProjectId: '',
    isDrawerOpen: false,
    expandedCycleId: null,
    manualDrawer: { isOpen: false, cycleId: '', cycleStatus: 'PENDING', itemId: '', itemTitle: '', itemStatus: 'PENDING', steps: [] },
    reviewExecutionId: null,
    expandedItemId: null,
};

function cycleReducer(state: CyclePageState, action: CyclePageAction): CyclePageState {
    switch (action.type) {
        case 'SET_PROJECT':
            return { ...state, selectedProjectId: action.projectId };
        case 'TOGGLE_DRAWER':
            return { ...state, isDrawerOpen: action.isOpen };
        case 'TOGGLE_EXPAND':
            return {
                ...state,
                expandedCycleId: state.expandedCycleId === action.cycleId ? null : action.cycleId,
            };
        case 'OPEN_MANUAL':
            return {
                ...state,
                manualDrawer: {
                    isOpen: true,
                    cycleId: action.cycleId,
                    cycleStatus: action.cycleStatus,
                    itemId: action.itemId,
                    itemTitle: action.itemTitle,
                    itemStatus: action.itemStatus,
                    steps: action.steps,
                },
            };
        case 'CLOSE_MANUAL':
            return { ...state, manualDrawer: { ...state.manualDrawer, isOpen: false } };
        case 'SET_REVIEW_EXECUTION':
            return { ...state, reviewExecutionId: action.executionId };
        case 'TOGGLE_ITEM_EXPAND':
            return {
                ...state,
                expandedItemId: state.expandedItemId === action.itemId ? null : action.itemId,
            };
        default:
            return state;
    }
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function formatDate(iso: string): string {
    try {
        return new Date(iso).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
        });
    } catch {
        return iso;
    }
}

const CYCLE_STATUS_STYLES: Record<string, string> = {
    PENDING: 'bg-amber-50 dark:bg-amber-950/30 text-amber-700 dark:text-amber-400 border-amber-200 dark:border-amber-800',
    RUNNING: 'bg-blue-50 dark:bg-blue-950/30 text-blue-700 dark:text-blue-400 border-blue-200 dark:border-blue-800',
    COMPLETED: 'bg-green-50 dark:bg-green-950/30 text-green-700 dark:text-green-400 border-green-200 dark:border-green-800',
};

const CYCLE_STATUS_ICONS: Record<string, React.ReactNode> = {
    PENDING: <Clock size={12} />,
    RUNNING: <Loader2 size={12} className="animate-spin" />,
    COMPLETED: <CheckCircle2 size={12} />,
};

const ITEM_STATUS_STYLES: Record<string, string> = {
    PENDING: 'bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400',
    RUNNING: 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-400',
    PASSED: 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-400',
    FAILED: 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-400',
    ERROR: 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-400',
    SKIPPED: 'bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-400',
};

const ITEM_STATUS_ICONS: Record<string, React.ReactNode> = {
    PENDING: <Clock size={11} />,
    RUNNING: <Loader2 size={11} className="animate-spin" />,
    PASSED: <CheckCircle2 size={11} />,
    FAILED: <XCircle size={11} />,
    ERROR: <AlertCircle size={11} />,
    SKIPPED: <SkipForward size={11} />,
};

// ── Sub-components ────────────────────────────────────────────────────────────

/** Renders the stat cells for a single cycle row (automation rate, pass/fail, total, created). */
function CycleStatCells({ cycle }: { cycle: ICycleRow }) {
    return (
        <>
            <td className="px-4 py-3">
                <div className="flex items-center gap-2">
                    <div className="w-16 h-1.5 rounded-full bg-slate-200 dark:bg-slate-700 overflow-hidden">
                        <div
                            className="h-full rounded-full bg-violet-500 dark:bg-violet-400 transition-all"
                            style={{ width: `${cycle.summary.automationRate}%` }}
                        />
                    </div>
                    <span className="text-xs text-slate-600 dark:text-slate-400 font-medium">
                        {cycle.summary.automationRate}%
                    </span>
                </div>
            </td>
            <td className="px-4 py-3">
                <div className="flex items-center gap-2 text-xs">
                    <span className="flex items-center gap-1 text-green-600 dark:text-green-400">
                        <CheckCircle2 size={12} />
                        {cycle.summary.passed}
                    </span>
                    <span className="text-slate-300 dark:text-slate-600">/</span>
                    <span className="flex items-center gap-1 text-red-600 dark:text-red-400">
                        <XCircle size={12} />
                        {cycle.summary.failed}
                    </span>
                </div>
            </td>
            <td className="px-4 py-3 text-slate-600 dark:text-slate-400">
                {cycle.summary.total}
            </td>
            <td className="px-4 py-3 text-slate-500 dark:text-slate-400 text-xs">
                {formatDate(cycle.createdAt)}
            </td>
        </>
    );
}

/** Renders a single item inside an expanded cycle (type icon, title, badges, action buttons). */
function CycleItemRow({
    item,
    cycle,
    isItemExpanded,
    expandedExecution,
    onPlayManual,
    onReviewExecution,
    onToggleItemExpand,
}: {
    item: ICycleItem;
    cycle: ICycleRow;
    isItemExpanded: boolean;
    expandedExecution?: Execution | null;
    onPlayManual: (cycle: ICycleRow, item: ICycleItem) => void;
    onReviewExecution: (executionId: string) => void;
    onToggleItemExpand: (itemId: string) => void;
}) {
    return (
        <div className="flex flex-col">
            <div
                className={`flex items-center gap-3 px-6 py-2.5 transition-colors duration-100 ${item.type === 'MANUAL'
                    ? 'cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-800/40'
                    : item.type === 'AUTOMATED' && item.executionId
                        ? 'cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-800/40'
                        : ''
                    }`}
                {...(item.type === 'MANUAL' && {
                    role: 'button' as const,
                    tabIndex: 0,
                    onClick: () => onPlayManual(cycle, item),
                    onKeyDown: (e: React.KeyboardEvent) => {
                        if (e.key === 'Enter' || e.key === ' ') onPlayManual(cycle, item);
                    },
                })}
            >
                {/* Expand toggle for AUTOMATED items */}
                {item.type === 'AUTOMATED' && item.executionId ? (
                    <button
                        onClick={(e) => { e.stopPropagation(); onToggleItemExpand(item.id); }}
                        className="shrink-0 text-slate-400 hover:text-slate-600 dark:text-slate-500 dark:hover:text-slate-300 transition-colors"
                        title={isItemExpanded ? 'Collapse test results' : 'Expand test results'}
                    >
                        {isItemExpanded
                            ? <ChevronDown size={13} />
                            : <ChevronRight size={13} />}
                    </button>
                ) : (
                    <div className="w-[13px] shrink-0" />
                )}

                {/* Type icon */}
                <div className="shrink-0">
                    {item.type === 'AUTOMATED' ? (
                        <Bot size={14} className="text-sky-600 dark:text-sky-400" />
                    ) : (
                        <ClipboardCheck size={14} className="text-violet-600 dark:text-violet-400" />
                    )}
                </div>

                {/* Title */}
                <p className="flex-1 min-w-0 text-sm text-slate-700 dark:text-slate-300 truncate">
                    {item.title}
                </p>

                {/* Type badge */}
                <span className="shrink-0 text-[10px] font-medium text-slate-400 dark:text-slate-500 uppercase">
                    {item.type}
                </span>

                {/* Status badge — clickable for AUTOMATED items with an execution */}
                {item.type === 'AUTOMATED' && item.executionId ? (
                    <button
                        title="Click to view execution logs"
                        onClick={(e) => {
                            e.stopPropagation();
                            onReviewExecution(item.executionId!);
                        }}
                        className={`shrink-0 inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-medium hover:opacity-75 transition-opacity ${ITEM_STATUS_STYLES[item.status] ?? ITEM_STATUS_STYLES.PENDING}`}
                    >
                        {ITEM_STATUS_ICONS[item.status] ?? ITEM_STATUS_ICONS.PENDING}
                        {item.status}
                    </button>
                ) : (
                    <span className={`shrink-0 inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-medium ${ITEM_STATUS_STYLES[item.status] ?? ITEM_STATUS_STYLES.PENDING}`}>
                        {ITEM_STATUS_ICONS[item.status] ?? ITEM_STATUS_ICONS.PENDING}
                        {item.status}
                    </span>
                )}

                {/* MANUAL: Execute (pending/running) or Review (completed) */}
                {item.type === 'MANUAL' && (item.status === 'PENDING' || item.status === 'RUNNING') && (
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            onPlayManual(cycle, item);
                        }}
                        className="shrink-0 flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-medium bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 hover:bg-green-200 dark:hover:bg-green-900/50 transition-colors"
                    >
                        <Play size={11} /> Execute
                    </button>
                )}
                {item.type === 'MANUAL' && item.status !== 'PENDING' && item.status !== 'RUNNING' && (
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            onPlayManual(cycle, item);
                        }}
                        className="shrink-0 flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-medium bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
                    >
                        <Eye size={11} /> {cycle.status === 'RUNNING' ? 'Re-mark' : 'Review'}
                    </button>
                )}

                {/* AUTOMATED: Logs button when execution exists */}
                {item.type === 'AUTOMATED' && item.executionId && (
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            onReviewExecution(item.executionId!);
                        }}
                        className="shrink-0 flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-medium bg-sky-100 dark:bg-sky-900/30 text-sky-700 dark:text-sky-400 hover:bg-sky-200 dark:hover:bg-sky-900/50 transition-colors"
                    >
                        <FileText size={11} /> Logs
                    </button>
                )}
            </div>

            {/* ── Drill-down panel: individual test results for AUTOMATED items ── */}
            {isItemExpanded && item.type === 'AUTOMATED' && (
                <div className="ml-10 border-l-2 border-slate-200 dark:border-slate-700 pl-4 py-2 mb-1">
                    {!expandedExecution ? (
                        <div className="flex items-center gap-2 text-xs text-slate-400 py-1">
                            <Loader2 size={11} className="animate-spin" />
                            Loading test results…
                        </div>
                    ) : expandedExecution.tests.length === 0 ? (
                        <p className="text-xs text-slate-400 py-1">No individual test results available.</p>
                    ) : (
                        <div className="space-y-0.5">
                            {expandedExecution.tests.map((t, idx) => (
                                <div key={t.testId ?? idx} className="flex items-start gap-2 py-1 text-xs">
                                    <span className={`shrink-0 inline-flex items-center gap-1 px-1.5 py-0.5 rounded font-medium ${
                                        t.status === 'passed' ? 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-400' :
                                        t.status === 'failed' ? 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-400' :
                                        t.status === 'timedOut' ? 'bg-orange-100 dark:bg-orange-900/40 text-orange-700 dark:text-orange-400' :
                                        'bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400'
                                    }`}>
                                        {t.status === 'passed' ? <CheckCircle2 size={10} /> : t.status === 'failed' ? <XCircle size={10} /> : <SkipForward size={10} />}
                                        {t.status}
                                    </span>
                                    <span className="flex-1 min-w-0 text-slate-700 dark:text-slate-300 truncate" title={t.name ?? t.testId}>
                                        {t.name ?? t.testId}
                                    </span>
                                    {t.duration != null && (
                                        <span className="shrink-0 text-slate-400 dark:text-slate-500 tabular-nums">
                                            {(t.duration / 1000).toFixed(1)}s
                                        </span>
                                    )}
                                    {t.error && (
                                        <span className="shrink-0 text-red-500 dark:text-red-400 truncate max-w-[200px]" title={t.error}>
                                            {t.error.slice(0, 80)}{t.error.length > 80 ? '…' : ''}
                                        </span>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

/** Renders a full cycle table row: the summary row + expanded items section. */
function CycleTableRow({
    cycle,
    isExpanded,
    expandedItemId,
    drillDownExecution,
    isViewer,
    isSelected,
    onToggle,
    onNavigateReport,
    onPlayManual,
    onReviewExecution,
    onToggleItemExpand,
    onDeleteCycle,
    onToggleSelect,
}: {
    cycle: ICycleRow;
    isExpanded: boolean;
    expandedItemId: string | null;
    drillDownExecution: Execution | null;
    isViewer: boolean;
    isSelected: boolean;
    onToggle: () => void;
    onNavigateReport: () => void;
    onPlayManual: (cycle: ICycleRow, item: ICycleItem) => void;
    onReviewExecution: (executionId: string) => void;
    onToggleItemExpand: (itemId: string) => void;
    onDeleteCycle: (cycle: ICycleRow) => void;
    onToggleSelect: (id: string) => void;
}) {
    return (
        <Fragment>
            {/* Cycle summary row */}
            <tr
                onClick={onToggle}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onToggle();
                    }
                }}
                tabIndex={0}
                className={`cursor-pointer hover:bg-slate-50 dark:hover:bg-gh-bg-subtle-dark transition-colors duration-100${isSelected ? ' bg-violet-50 dark:bg-violet-950/20' : ''}`}
            >
                {/* Checkbox column */}
                <td className="pl-4 py-3 w-8" onClick={(e) => e.stopPropagation()}>
                    <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => onToggleSelect(cycle._id)}
                        aria-label={`Select cycle "${cycle.name}"`}
                        className="w-4 h-4 rounded border-slate-300 dark:border-slate-600 text-violet-600 focus:ring-violet-500 cursor-pointer accent-violet-600"
                    />
                </td>
                <td className="px-3 py-3">
                    {isExpanded
                        ? <ChevronDown size={14} className="text-slate-400" />
                        : <ChevronRight size={14} className="text-slate-400" />}
                </td>
                <td className="px-4 py-3">
                    <p className="font-medium text-slate-900 dark:text-slate-100 truncate max-w-xs">
                        {cycle.name}
                    </p>
                    {cycle.ciContext && (
                        <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                            {cycle.ciContext.branch && (
                                <span className="inline-flex items-center gap-1 text-[10px] text-slate-400 dark:text-slate-500">
                                    <GitBranch size={10} />
                                    {cycle.ciContext.branch}
                                </span>
                            )}
                            {cycle.ciContext.repository && (
                                <span className="text-[10px] text-slate-400 dark:text-slate-500 truncate max-w-[160px]">
                                    {cycle.ciContext.repository}
                                </span>
                            )}
                            {cycle.ciContext.prNumber && (
                                <span className="text-[10px] text-violet-500 dark:text-violet-400 font-medium">
                                    #{cycle.ciContext.prNumber}
                                </span>
                            )}
                        </div>
                    )}
                </td>
                <td className="px-4 py-3">
                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium border ${CYCLE_STATUS_STYLES[cycle.status] ?? ''}`}>
                        {CYCLE_STATUS_ICONS[cycle.status]}
                        {cycle.status}
                    </span>
                </td>
                <CycleStatCells cycle={cycle} />
                <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={onNavigateReport}
                            title="View cycle report"
                            className="flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] font-medium bg-violet-100 dark:bg-violet-950/40 text-violet-700 dark:text-violet-400 hover:bg-violet-200 dark:hover:bg-violet-900/50 transition-colors"
                        >
                            <Eye size={11} />
                            View Report
                        </button>
                        {!isViewer && (
                            <button
                                onClick={(e) => { e.stopPropagation(); onDeleteCycle(cycle); }}
                                title="Delete cycle"
                                className="flex items-center justify-center w-7 h-7 rounded-md text-slate-400 hover:text-red-600 hover:bg-red-50 dark:hover:text-red-400 dark:hover:bg-red-950/30 transition-colors"
                            >
                                <Trash2 size={13} />
                            </button>
                        )}
                    </div>
                </td>
            </tr>

            {/* Expanded items section */}
            {isExpanded && cycle.items && cycle.items.length > 0 && (
                <tr>
                    <td colSpan={8} className="p-0">
                        <div className="bg-slate-50 dark:bg-gh-bg-subtle-dark border-t border-slate-200 dark:border-gh-border-dark">
                            <div className="px-6 py-2 border-b border-slate-200 dark:border-gh-border-dark">
                                <p className="text-[10px] font-semibold text-slate-400 dark:text-slate-500 uppercase tracking-widest">
                                    Cycle Items ({cycle.items.length})
                                </p>
                            </div>
                            <div className="divide-y divide-slate-200 dark:divide-gh-border-dark">
                                {cycle.items.map((item) => (
                                    <CycleItemRow
                                        key={item.id}
                                        item={item}
                                        cycle={cycle}
                                        isItemExpanded={expandedItemId === item.id}
                                        expandedExecution={expandedItemId === item.id ? drillDownExecution : null}
                                        onPlayManual={onPlayManual}
                                        onReviewExecution={onReviewExecution}
                                        onToggleItemExpand={onToggleItemExpand}
                                    />
                                ))}
                            </div>
                        </div>
                    </td>
                </tr>
            )}

            {isExpanded && (!cycle.items || cycle.items.length === 0) && (
                <tr>
                    <td colSpan={8} className="px-6 py-4 text-center text-sm text-slate-400 dark:text-slate-500 bg-slate-50 dark:bg-gh-bg-subtle-dark">
                        No items in this cycle.
                    </td>
                </tr>
            )}
        </Fragment>
    );
}

// ── Main Component ────────────────────────────────────────────────────────────

export function TestCycles() {
    const { token, user } = useAuth();
    const queryClient = useQueryClient();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const { activeProjectId: ctxProjectId, setActiveProjectId: setCtxProjectId } = useProjectContext();
    const [state, dispatch] = useReducer(cycleReducer, {
        ...initialState,
        selectedProjectId: searchParams.get('project') ?? ctxProjectId ?? '',
    });

    // ── Search & filter state (URL-synced) ──────────────────────────────────
    const [searchInput, setSearchInput] = useState(searchParams.get('search') ?? '');
    const [debouncedSearch, setDebouncedSearch] = useState(searchParams.get('search') ?? '');
    const [statusFilter, setStatusFilter] = useState(searchParams.get('status') ?? '');
    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    /** Update a single URL param while preserving all others. */
    function updateParam(key: string, value: string) {
        setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            if (value) next.set(key, value);
            else next.delete(key);
            return next;
        }, { replace: true });
    }

    function handleSearchChange(value: string) {
        setSearchInput(value);
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
            setDebouncedSearch(value);
            updateParam('search', value);
        }, 350);
    }

    function handleStatusFilterChange(value: string) {
        setStatusFilter(value);
        updateParam('status', value);
    }

    // When header selector changes context, update reducer + URL.
    // No truthiness guard — ctxProjectId = '' means "All Projects" and must propagate.
    useEffect(() => {
        if (ctxProjectId !== state.selectedProjectId) {
            dispatch({ type: 'SET_PROJECT', projectId: ctxProjectId });
            setSearchParams((prev) => {
                const next = new URLSearchParams(prev);
                if (ctxProjectId) next.set('project', ctxProjectId);
                else next.delete('project');
                return next;
            }, { replace: true });
        }
    }, [ctxProjectId]);
    const [activeTab, setActiveTab] = useState<TabId>('manual');
    const [deletingCycle, setDeletingCycle] = useState<ICycleRow | null>(null);
    const [isDeletingCycle, setIsDeletingCycle] = useState(false);
    const isViewer = user?.role === 'viewer';

    // ── Bulk-select state ────────────────────────────────────────────────
    const [selectedCycleIds, setSelectedCycleIds] = useState<Set<string>>(new Set());
    const [isBulkDeleting, setIsBulkDeleting] = useState(false);

    // Clear selection when tab or visible cycle list changes
    useEffect(() => {
        setSelectedCycleIds(new Set());
    }, [activeTab, debouncedSearch, statusFilter, state.selectedProjectId]);

    // ── Fetch projects ──────────────────────────────────────────────────────
    const {
        data: projectsData,
        isLoading: projectsLoading,
    } = useQuery<{ projects: IProject[] }>({
        queryKey: ['projects', token],
        queryFn: async () => {
            const { data } = await axios.get(`${API_URL}/api/projects`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            return data;
        },
        enabled: !!token,
        staleTime: 60_000,
    });

    const projects = projectsData?.projects ?? [];
    // No fallback to projects[0] — empty string means "All Projects" and is intentional.
    const effectiveProjectId = state.selectedProjectId;

    // ── Fetch test cycles ───────────────────────────────────────────────────
    const {
        data: cyclesData,
        isLoading: cyclesLoading,
        isError: cyclesError,
    } = useQuery<{ cycles: ICycleRow[] }>({
        queryKey: ['test-cycles', effectiveProjectId || 'all', debouncedSearch, statusFilter, token],
        queryFn: async () => {
            const params = new URLSearchParams();
            if (effectiveProjectId) params.set('projectId', effectiveProjectId);
            if (debouncedSearch) params.set('search', debouncedSearch);
            if (statusFilter) params.set('status', statusFilter);
            const qs = params.toString();
            const url = `${API_URL}/api/test-cycles${qs ? `?${qs}` : ''}`;
            const { data } = await axios.get(url, { headers: { Authorization: `Bearer ${token}` } });
            return data.data;
        },
        enabled: !!token,
        staleTime: 30_000,
    });

    const cycles = cyclesData?.cycles ?? [];

    const isCiCycle = (c: ICycleRow) => c.source === 'external-ci' || !!c.ciContext;

    const visibleCycles = cycles.filter((c) =>
        activeTab === 'ci' ? isCiCycle(c) : !isCiCycle(c),
    );

    const manualCount = cycles.filter((c) => !isCiCycle(c)).length;
    const ciCount = cycles.filter(isCiCycle).length;

    // ── Fetch execution for automated review drawer ─────────────────────────
    const { data: reviewExecutionResponse } = useQuery<{ execution: Execution } | null>({
        queryKey: ['execution-review', state.reviewExecutionId, token],
        queryFn: async () => {
            const { data } = await axios.get(
                `${API_URL}/api/executions/${state.reviewExecutionId}`,
                { headers: { Authorization: `Bearer ${token}` } },
            );
            return data.success ? data.data : null;
        },
        enabled: !!token && !!state.reviewExecutionId,
        staleTime: 30_000,
        refetchInterval: (query: any) => {
            const exec = query?.state?.data?.execution || query?.data?.execution;
            if (exec?.status === 'RUNNING' || exec?.status === 'ANALYZING') return 3000;
            if (!exec && state.reviewExecutionId) return 3000;
            return false;
        },
    });

    const reviewExecution: Execution | null = reviewExecutionResponse?.execution ?? null;

    // Derive before the hook so it can be used in queryKey + enabled.
    const expandedItemExecutionId = (() => {
        if (!state.expandedItemId) return null;
        for (const cycle of cycles) {
            const found = cycle.items.find((i) => i.id === state.expandedItemId);
            if (found?.executionId) return found.executionId;
        }
        return null;
    })();

    // ── Fetch expanded automated item execution (drill-down) ────────────────
    const { data: drillDownResponse } = useQuery<{ execution: Execution } | null>({
        queryKey: ['execution-drilldown', expandedItemExecutionId, token],
        queryFn: async () => {
            const { data } = await axios.get(
                `${API_URL}/api/executions/${expandedItemExecutionId}`,
                { headers: { Authorization: `Bearer ${token}` } },
            );
            return data.success ? data.data : null;
        },
        enabled: !!token && !!expandedItemExecutionId,
        staleTime: 30_000,
    });

    const drillDownExecution: Execution | null = drillDownResponse?.execution ?? null;

    // ── Handlers ────────────────────────────────────────────────────────────

    function openManualPlayer(cycle: ICycleRow, item: ICycleItem) {
        dispatch({
            type: 'OPEN_MANUAL',
            cycleId: cycle._id,
            cycleStatus: cycle.status,
            itemId: item.id,
            itemTitle: item.title,
            itemStatus: item.status,
            steps: (item.manualSteps ?? []).map((s) => ({
                id: s.id,
                action: s.action,
                expectedResult: s.expectedResult,
                status: s.status as 'PENDING' | 'PASSED' | 'FAILED' | 'SKIPPED',
                comment: s.comment,
            })),
        });
    }

    function openExecutionReview(executionId: string) {
        dispatch({ type: 'SET_REVIEW_EXECUTION', executionId });
    }

    async function handleDeleteCycle() {
        if (!deletingCycle || isDeletingCycle) return;
        setIsDeletingCycle(true);
        try {
            await axios.delete(`${API_URL}/api/test-cycles/${deletingCycle._id}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setDeletingCycle(null);
            queryClient.invalidateQueries({ queryKey: ['test-cycles'] });
        } catch (err: any) {
            const msg = err?.response?.data?.error ?? 'Failed to delete cycle.';
            alert(msg);
        } finally {
            setIsDeletingCycle(false);
        }
    }

    // ── Bulk-select handlers ─────────────────────────────────────────────

    const handleToggleSelect = useCallback((id: string) => {
        setSelectedCycleIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    }, []);

    const handleSelectAll = useCallback(() => {
        setSelectedCycleIds((prev) =>
            prev.size === visibleCycles.length && visibleCycles.length > 0
                ? new Set()
                : new Set(visibleCycles.map((c) => c._id)),
        );
    }, [visibleCycles]);

    const isAllSelected = visibleCycles.length > 0 && selectedCycleIds.size === visibleCycles.length;
    const isPartiallySelected = selectedCycleIds.size > 0 && !isAllSelected;

    async function handleBulkDelete() {
        if (isBulkDeleting || selectedCycleIds.size === 0) return;
        if (!window.confirm(`Delete ${selectedCycleIds.size} selected cycle${selectedCycleIds.size > 1 ? 's' : ''}? This cannot be undone.`)) return;
        setIsBulkDeleting(true);
        try {
            const res = await axios.delete(`${API_URL}/api/test-cycles/bulk`, {
                headers: { Authorization: `Bearer ${token}` },
                data: { ids: [...selectedCycleIds] },
            });
            const { deleted, skipped } = res.data;
            setSelectedCycleIds(new Set());
            queryClient.invalidateQueries({ queryKey: ['test-cycles'] });
            if (skipped > 0) {
                alert(`Deleted ${deleted} cycle${deleted !== 1 ? 's' : ''}. ${skipped} RUNNING cycle${skipped !== 1 ? 's were' : ' was'} skipped.`);
            }
        } catch (err: any) {
            const msg = err?.response?.data?.error ?? 'Bulk delete failed. Please try again.';
            alert(msg);
        } finally {
            setIsBulkDeleting(false);
        }
    }

    // ── Render ──────────────────────────────────────────────────────────────

    return (
        <div className="flex flex-col h-full gap-6 p-6 md:p-8">

            {/* ── Page header ─────────────────────────────────────────── */}
            <div className="flex items-center justify-between gap-4 flex-wrap">
                <div className="flex items-center gap-3 min-w-0">
                    <div className="flex items-center justify-center w-9 h-9 rounded-lg bg-violet-50 dark:bg-violet-950/40 shrink-0">
                        <Layers size={18} className="text-violet-600 dark:text-violet-400" />
                    </div>
                    <div className="min-w-0">
                        <h1 className="text-lg font-semibold text-slate-900 dark:text-slate-100 leading-tight">
                            Test Cycles
                        </h1>
                        <p className="text-xs text-slate-500 dark:text-slate-400">
                            Hybrid cycles grouping manual and automated tests
                        </p>
                    </div>
                </div>

                {!isViewer && activeTab === 'manual' && (
                    <button
                        onClick={() => dispatch({ type: 'TOGGLE_DRAWER', isOpen: true })}
                        disabled={!effectiveProjectId}
                        className="flex items-center gap-2 px-4 py-2 rounded-lg bg-violet-600 hover:bg-violet-700 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-medium transition-colors duration-150 shrink-0"
                    >
                        <Plus size={16} />
                        Create Cycle
                    </button>
                )}
            </div>

            {/* ── Tabs navigation ──────────────────────────────────────── */}
            <div className="flex items-center gap-1 p-1 rounded-lg bg-slate-100 dark:bg-slate-800/60 self-start">
                <button
                    onClick={() => setActiveTab('manual')}
                    className={`flex items-center gap-2 px-4 py-1.5 rounded-md text-sm font-medium transition-colors duration-150 ${
                        activeTab === 'manual'
                            ? 'bg-white dark:bg-gh-bg-dark text-violet-700 dark:text-violet-400 shadow-sm'
                            : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300'
                    }`}
                >
                    <ClipboardCheck size={14} />
                    Manual Cycles
                    {manualCount > 0 && (
                        <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${
                            activeTab === 'manual'
                                ? 'bg-violet-100 dark:bg-violet-950/60 text-violet-600 dark:text-violet-400'
                                : 'bg-slate-200 dark:bg-slate-700 text-slate-500 dark:text-slate-400'
                        }`}>
                            {manualCount}
                        </span>
                    )}
                </button>
                <button
                    onClick={() => setActiveTab('ci')}
                    className={`flex items-center gap-2 px-4 py-1.5 rounded-md text-sm font-medium transition-colors duration-150 ${
                        activeTab === 'ci'
                            ? 'bg-white dark:bg-gh-bg-dark text-sky-700 dark:text-sky-400 shadow-sm'
                            : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300'
                    }`}
                >
                    <Cpu size={14} />
                    CI / Automated
                    {ciCount > 0 && (
                        <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${
                            activeTab === 'ci'
                                ? 'bg-sky-100 dark:bg-sky-950/60 text-sky-600 dark:text-sky-400'
                                : 'bg-slate-200 dark:bg-slate-700 text-slate-500 dark:text-slate-400'
                        }`}>
                            {ciCount}
                        </span>
                    )}
                </button>
            </div>


            {/* ── Filter bar ──────────────────────────────────────────── */}
            <div className="flex items-center gap-3 flex-wrap">
                {/* Search input */}
                <div className="relative">
                    <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                    <input
                        type="text"
                        value={searchInput}
                        onChange={(e) => handleSearchChange(e.target.value)}
                        placeholder="Search cycles…"
                        className="pl-8 pr-7 py-1.5 text-sm w-56 rounded-lg border border-slate-200 dark:border-gh-border-dark bg-white dark:bg-gh-bg-dark text-slate-900 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent transition"
                    />
                    {searchInput && (
                        <button
                            onClick={() => handleSearchChange('')}
                            className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors"
                            aria-label="Clear search"
                        >
                            <X size={13} />
                        </button>
                    )}
                </div>

                {/* Status filter */}
                <select
                    value={statusFilter}
                    onChange={(e) => handleStatusFilterChange(e.target.value)}
                    className="px-3 py-1.5 text-sm rounded-lg border border-slate-200 dark:border-gh-border-dark bg-white dark:bg-gh-bg-dark text-slate-700 dark:text-slate-300 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent transition cursor-pointer"
                >
                    <option value="">All Statuses</option>
                    <option value="PENDING">Pending</option>
                    <option value="RUNNING">Running</option>
                    <option value="COMPLETED">Completed</option>
                </select>

                {/* Active filter chips */}
                {(debouncedSearch || statusFilter) && (
                    <button
                        onClick={() => { handleSearchChange(''); handleStatusFilterChange(''); }}
                        className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 border border-slate-200 dark:border-gh-border-dark hover:border-slate-300 dark:hover:border-slate-600 transition-colors"
                    >
                        <X size={11} />
                        Clear filters
                    </button>
                )}
            </div>

            {/* ── Cycles table ────────────────────────────────────────── */}
            <div className="flex-1 min-h-0 overflow-y-auto rounded-xl border border-slate-200 dark:border-gh-border-dark bg-white dark:bg-gh-bg-dark">

                {cyclesLoading ? (
                    <div className="flex items-center justify-center gap-2 h-48 text-slate-500 dark:text-slate-400">
                        <Loader2 size={18} className="animate-spin" />
                        <span className="text-sm">Loading test cycles…</span>
                    </div>
                ) : cyclesError ? (
                    <div className="flex flex-col items-center justify-center gap-3 h-48 text-red-600 dark:text-red-400">
                        <AlertCircle size={24} />
                        <p className="text-sm">Failed to load test cycles. Please try again.</p>
                    </div>
                ) : visibleCycles.length === 0 ? (
                    <div className="flex flex-col items-center justify-center gap-3 h-48 text-slate-400 dark:text-slate-500">
                        {(debouncedSearch || statusFilter) ? (
                            <>
                                <Search size={32} strokeWidth={1.5} />
                                <p className="text-sm font-medium">No cycles match your filters</p>
                                <button
                                    onClick={() => { handleSearchChange(''); handleStatusFilterChange(''); }}
                                    className="text-xs text-violet-600 dark:text-violet-400 hover:underline"
                                >
                                    Clear filters
                                </button>
                            </>
                        ) : activeTab === 'ci' ? (
                            <>
                                <Cpu size={32} strokeWidth={1.5} />
                                <p className="text-sm font-medium">No CI runs yet</p>
                                <p className="text-xs text-center max-w-xs">
                                    Trigger a run from your CI pipeline or via the PR webhook to see automated cycles here.
                                </p>
                            </>
                        ) : (
                            <>
                                <FolderKanban size={32} strokeWidth={1.5} />
                                <p className="text-sm font-medium">No manual cycles yet</p>
                                <p className="text-xs text-center max-w-xs">
                                    Click &quot;Create Cycle&quot; to build your first hybrid test cycle.
                                </p>
                            </>
                        )}
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="sticky top-0 z-10">
                                <tr className="border-b border-slate-200 dark:border-gh-border-dark bg-slate-50 dark:bg-gh-bg-subtle-dark">
                                    {/* Select-all checkbox */}
                                    {!isViewer && (
                                        <th className="pl-4 py-3 w-8" onClick={(e) => e.stopPropagation()}>
                                            <input
                                                type="checkbox"
                                                checked={isAllSelected}
                                                ref={(el) => { if (el) el.indeterminate = isPartiallySelected; }}
                                                onChange={handleSelectAll}
                                                aria-label="Select all cycles"
                                                className="w-4 h-4 rounded border-slate-300 dark:border-slate-600 text-violet-600 focus:ring-violet-500 cursor-pointer accent-violet-600"
                                            />
                                        </th>
                                    )}
                                    {/* Expand chevron column */}
                                    <th className="px-3 py-3 w-8" />
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                                        Cycle Name
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                                        Status
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                                        Automation
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                                        Pass / Fail
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                                        Total
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                                        Created
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                                        Report
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100 dark:divide-gh-border-dark">
                                {visibleCycles.map((cycle) => (
                                    <CycleTableRow
                                        key={cycle._id}
                                        cycle={cycle}
                                        isExpanded={state.expandedCycleId === cycle._id}
                                        expandedItemId={state.expandedItemId}
                                        drillDownExecution={drillDownExecution}
                                        isViewer={isViewer}
                                        isSelected={selectedCycleIds.has(cycle._id)}
                                        onToggle={() => dispatch({ type: 'TOGGLE_EXPAND', cycleId: cycle._id })}
                                        onNavigateReport={() => navigate(`/test-cycles/${cycle._id}/report`)}
                                        onPlayManual={openManualPlayer}
                                        onReviewExecution={openExecutionReview}
                                        onToggleItemExpand={(itemId) => dispatch({ type: 'TOGGLE_ITEM_EXPAND', itemId })}
                                        onDeleteCycle={setDeletingCycle}
                                        onToggleSelect={handleToggleSelect}
                                    />
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* ── Floating Bulk Actions Bar ──────────────────────────────── */}
            {!isViewer && selectedCycleIds.size > 0 && (
                <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 flex items-center gap-3 px-5 py-3 rounded-2xl bg-slate-900 dark:bg-slate-800 shadow-2xl border border-slate-700 dark:border-slate-600 text-white animate-in slide-in-from-bottom-4 duration-200">
                    <CheckSquare size={16} className="text-violet-400 shrink-0" />
                    <span className="text-sm font-medium">
                        {selectedCycleIds.size} cycle{selectedCycleIds.size !== 1 ? 's' : ''} selected
                    </span>
                    <div className="w-px h-5 bg-slate-600" />
                    <button
                        onClick={() => setSelectedCycleIds(new Set())}
                        className="text-xs text-slate-400 hover:text-slate-200 transition-colors"
                    >
                        Clear
                    </button>
                    <button
                        onClick={handleBulkDelete}
                        disabled={isBulkDeleting}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-red-600 hover:bg-red-500 disabled:opacity-60 text-white text-sm font-medium transition-colors"
                    >
                        {isBulkDeleting
                            ? <Loader2 size={13} className="animate-spin" />
                            : <Trash2 size={13} />}
                        Delete Selected
                    </button>
                </div>
            )}

            {/* ── Cycle Builder Drawer ──────────────────────────────────── */}
            <CycleBuilderDrawer
                isOpen={state.isDrawerOpen}
                projectId={effectiveProjectId}
                onClose={() => dispatch({ type: 'TOGGLE_DRAWER', isOpen: false })}
                onCreated={() => queryClient.invalidateQueries({ queryKey: ['test-cycles'] })}
            />

            {/* ── Manual Execution / Review Drawer ─────────────────────── */}
            <ManualExecutionDrawer
                isOpen={state.manualDrawer.isOpen}
                cycleId={state.manualDrawer.cycleId}
                itemId={state.manualDrawer.itemId}
                itemTitle={state.manualDrawer.itemTitle}
                itemStatus={state.manualDrawer.itemStatus}
                initialSteps={state.manualDrawer.steps}
                isEditable={state.manualDrawer.cycleStatus === 'RUNNING'}
                onClose={() => dispatch({ type: 'CLOSE_MANUAL' })}
            />

            {/* ── Automated Execution Review Drawer ────────────────────── */}
            <ExecutionDrawer
                executionId={state.reviewExecutionId}
                execution={reviewExecution}
                onClose={() => dispatch({ type: 'SET_REVIEW_EXECUTION', executionId: null })}
            />

            {/* ── Delete Cycle Confirmation Dialog ─────────────────────── */}
            <Transition show={!!deletingCycle} as={Fragment}>
                <Dialog onClose={() => { if (!isDeletingCycle) setDeletingCycle(null); }} className="relative z-50">
                    <TransitionChild
                        as={Fragment}
                        enter="ease-out duration-200"
                        enterFrom="opacity-0"
                        enterTo="opacity-100"
                        leave="ease-in duration-150"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                    >
                        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm" aria-hidden="true" />
                    </TransitionChild>

                    <div className="fixed inset-0 flex items-center justify-center p-4">
                        <TransitionChild
                            as={Fragment}
                            enter="ease-out duration-200"
                            enterFrom="opacity-0 scale-95"
                            enterTo="opacity-100 scale-100"
                            leave="ease-in duration-150"
                            leaveFrom="opacity-100 scale-100"
                            leaveTo="opacity-0 scale-95"
                        >
                            <DialogPanel className="w-full max-w-md rounded-xl bg-white dark:bg-gh-bg-dark shadow-xl border border-slate-200 dark:border-gh-border-dark p-6">
                                <div className="flex items-start justify-between mb-4">
                                    <div className="flex items-center gap-3">
                                        <div className="flex items-center justify-center w-9 h-9 rounded-full bg-red-100 dark:bg-red-950/40 shrink-0">
                                            <Trash2 size={18} className="text-red-600 dark:text-red-400" />
                                        </div>
                                        <div>
                                            <h2 className="text-base font-semibold text-slate-900 dark:text-slate-100">
                                                Delete Cycle
                                            </h2>
                                            <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                                                This action cannot be undone.
                                            </p>
                                        </div>
                                    </div>
                                    <button
                                        onClick={() => setDeletingCycle(null)}
                                        disabled={isDeletingCycle}
                                        className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors disabled:opacity-50"
                                    >
                                        <X size={18} />
                                    </button>
                                </div>

                                <p className="text-sm text-slate-600 dark:text-slate-400 mb-6">
                                    Delete cycle{' '}
                                    <span className="font-semibold text-slate-900 dark:text-slate-100">
                                        &ldquo;{deletingCycle?.name}&rdquo;
                                    </span>
                                    ? All associated results will be permanently removed.
                                </p>

                                <div className="flex gap-3 justify-end">
                                    <button
                                        onClick={() => setDeletingCycle(null)}
                                        disabled={isDeletingCycle}
                                        className="px-4 py-2 text-sm font-medium rounded-lg border border-slate-300 dark:border-gh-border-dark text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 disabled:opacity-50 transition-colors"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        onClick={handleDeleteCycle}
                                        disabled={isDeletingCycle}
                                        className="flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg bg-red-600 hover:bg-red-700 text-white disabled:opacity-50 transition-colors"
                                    >
                                        {isDeletingCycle ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                                        Delete Cycle
                                    </button>
                                </div>
                            </DialogPanel>
                        </TransitionChild>
                    </div>
                </Dialog>
            </Transition>
        </div>
    );
}
